import React from 'react';
import I18n from 'i18n-js';
import classNames from 'classnames';
import { CoreBumpRecommendationListingCard } from '@reverbdotcom/commons/src/gql/graphql';
import FormGroupWithRadio from '@reverbdotcom/commons/src/components/form_group_with_radio';
import { formatBid, bumpRatePercentToDecimal } from './bump_helpers';
import {
  MAX_BUMP_AMOUNT_PERCENT,
  MIN_BUMP_AMOUNT_PERCENT,
  BUMP_INCREMENT_AMOUNT_PERCENT,
} from '../shared/constants';
import { RCTag, RCAlertBox } from '@reverbdotcom/cadence/components';

interface Props {
  listing: CoreBumpRecommendationListingCard.Fragment;
  recommendationAmount: number;
  selectedBid: number;
  handleBidChange: (bid: number) => void;
  showCustomWarning: boolean;
  setShowCustomWarning: (boolean) => void;

  updateBid: (
    bid: number,
    bumpRecommendationAmount: number,
    listing: CoreBumpRecommendationListingCard.Fragment,
    refetch: (newBid: number | null) => void,
  ) => void;
  refetch: (newBid: number | null) => void;
}

export function RecommendedRadio(props: Props) {
  const [usingCustom, setUsingCustom] = React.useState(
    (props.selectedBid !== 0 && ((bumpRatePercentToDecimal(props.recommendationAmount)) !== props.selectedBid)),
  );

  const [usingRecommended, setUsingRecommended] = React.useState(
    !usingCustom,
  );
  const [usingNoBump, setUsingNoBump] = React.useState(
    false,
  );

  const [customBid, setCustomBid] = React.useState(
    (props.selectedBid !== 0) ? (formatBid(props.selectedBid)) : props.recommendationAmount,
  );

  React.useEffect(() => {
    if (usingRecommended) {
      props.handleBidChange(bumpRatePercentToDecimal(props.recommendationAmount));
    }
  }, [usingRecommended]);

  const handleRecommendedClick = (): void => {
    setUsingRecommended(true);
    setUsingCustom(false);
    setUsingNoBump(false);
    props.setShowCustomWarning(false);

    props.handleBidChange(bumpRatePercentToDecimal(props.recommendationAmount));
  };

  const handleManualClick = (): void => {
    setUsingRecommended(false);
    setUsingCustom(true);
    setUsingNoBump(false);

    setCustomWarning(customBid);
    props.handleBidChange(bumpRatePercentToDecimal(customBid));
  };

  const handleNoBumpClick = (): void => {
    setUsingRecommended(false);
    setUsingCustom(false);
    setUsingNoBump(true);
    props.setShowCustomWarning(false);

    props.handleBidChange(0);
  };

  const handleCustomSelectChange = (event: any) => {
    const bid = event?.target?.value;
    setCustomWarning(bid);
    setCustomBid(bid);
    props.handleBidChange(bumpRatePercentToDecimal(bid));
  };

  const setCustomWarning = (bid: number) => {
    if ((bid >= MIN_BUMP_AMOUNT_PERCENT) && (bid <= MAX_BUMP_AMOUNT_PERCENT) && (bid % BUMP_INCREMENT_AMOUNT_PERCENT === 0)) {
      props.setShowCustomWarning(false);
    } else {
      props.setShowCustomWarning(true);
    }
  };

  return (
    <>
      <div className={classNames(
        'bd-2 bd-radius-primary padding-4',
        { 'bd--blue': usingRecommended },
        { 'bd-primary': !(usingRecommended) },
      )}>
        <div className="d-flex fx-justify-between">
          <FormGroupWithRadio
            label={I18n.t('discovery.bumpModalV2.copyV2.recommendedRate', { percent: props.recommendationAmount })}
            inputName="bump-modal-radios"
            id="bump-modal-radio-recommended"
            updateField={handleRecommendedClick}
            value={`${props.recommendationAmount / 100}`}
            checked={usingRecommended}
          />
          <div className="ml-2">
            <RCTag>
              {I18n.t('discovery.bumpModalV2.modal.recommended')}
            </RCTag>
          </div>
        </div>
        <p className="size-90 mb-0">
          {I18n.t('discovery.bumpModalV2.copyV2.recommendedRateDescription')}
        </p>
      </div>

      <div className={classNames(
        'bd-2 bd-radius-primary padding-4 mt-2',
        { 'bd--blue': usingCustom },
        { 'bd-primary': !(usingCustom) },
      )}>
        <FormGroupWithRadio
          label={I18n.t('discovery.bumpModalV2.copyV2.customRate')}
          inputName="bump-modal-radios"
          id="bump-modal-radio-custom"
          updateField={handleManualClick}
          value={`${props.recommendationAmount / 100}`}
          checked={usingCustom}
        />
        {
          usingCustom &&
          <div className="width-20 mobile-width-100">
            <div className="input-group">
              <input
                type="number"
                className="input-group__input"
                disabled={!(usingCustom)}
                value={customBid}
                onChange={handleCustomSelectChange}
              />
              <div className="input-group__text">%</div>
            </div>
          </div>
        }
        {
          props.showCustomWarning && <div className="mt-2">
            <RCAlertBox type="error" small>
              {I18n.t('discovery.bumpModalV2.modal.customBumpWarning')}
            </RCAlertBox>
          </div>
        }
      </div>

      <div className={classNames(
        'bd-2 bd-radius-primary padding-4 mt-2',
        { 'bd--blue': usingNoBump },
        { 'bd-primary': !(usingNoBump) },
      )}>
        <FormGroupWithRadio
          label={I18n.t('discovery.bumpModalV2.copyV2.optOut')}
          inputName="bump-modal-radios"
          id="bump-modal-radio-no-bump"
          updateField={handleNoBumpClick}
          value={`${0}`}
          checked={usingNoBump}
        />
      </div>
    </>
  );
}
